<template>
  <div>
    <h1>{{ $t('views.yourPersonalData.title') }}</h1>
    <loading-container :state="state">
      <link-shopping-cart />
      <form @submit.prevent="onSubmit()">
        <control-select
          i18nLabel="common.owner.salutation"
          :validation="v$.salutation"
          v-model="salutation"
          :availableValues="salutationOptions()"
        />
        <control-input
          i18nLabel="common.owner.title"
          :validation="v$.title"
          v-model="title"
        />
        <control-input
          i18nLabel="common.owner.givenName"
          :validation="v$.givenName"
          v-model="givenName"
        />
        <control-input
          i18nLabel="common.owner.surName"
          :validation="v$.surName"
          v-model="surName"
        />
        <control-input
          i18nLabel="common.owner.organization"
          :validation="v$.organization"
          v-model="organization"
        />
        <control-input
          i18nLabel="common.owner.email"
          :validation="v$.email"
          v-model="email"
          type="email"
        />
        <control-input
          i18nLabel="common.owner.phone"
          :validation="v$.phone"
          v-model="phone"
          type="tel"
        />
        <control-checkbox
          i18nLabel="common.owner.accessible"
          :validation="v$.accessible"
          v-model="accessible"
        />
        <p class="mb-2"><small>{{ $t('views.yourPersonalData.requiredFieldsText') }}</small></p>
        <p class="mb-2"><small>{{ $t('views.yourPersonalData.infoText') }} <a :href="sanitizeUrl($t('views.yourPersonalData.infoTextLink'))" :title="$t('views.yourPersonalData.infoTextLinkTitle')" target="_blank" rel="noopener noreferrer">{{ $t('views.yourPersonalData.infoTextLinkTitle') }}</a></small></p>
        <p><small>{{ $t('views.yourPersonalData.securityText') }}</small></p>
        <div class="row mt-3">
          <div class="col-12 col-md-6 offset-md-3 col-lg-4 offset-lg-4">
            <button-action i18nMessage="views.yourPersonalData.proceedToSummary" v-on:clicked="onSubmit()" class="btn-w-100" :btnOutline="true" />
          </div>
        </div>
      </form>

      <button-panic class="btn-to-danger-link d-flex justify-content-end pr-3 mt-5" />

    </loading-container>

    <div class="col-12 d-flex justify-content-end mt-3 px-0">
      <link-faq />
    </div>

  </div>
</template>

<script lang="ts">
import ControlCheckbox from '@/components/controls/ControlCheckbox.vue'
import ControlInput from '@/components/controls/ControlInput.vue'
import ControlSelect from '@/components/controls/ControlSelect.vue'
import ButtonAction from '@/components/interaction/ButtonAction.vue'
import LinkShoppingCart from '@/components/interaction/LinkShoppingCart.vue'
import LoadingContainer from '@/components/structure/LoadingContainer.vue'
import LoadingMixin from '@/mixins/LoadingMixin'
import { PersonalData } from '@/models/PersonalData'
import { SelectOption } from '@/models/SelectOption'
import { userService } from '@/services/userService'
import { createSalutationOptions, validateAndFocusInvalid } from '@/utils/formUtils'
import { LoadingState } from '@/utils/loadingUtils'
import useVuelidate from '@vuelidate/core'
import { maxLength, required, email} from '@vuelidate/validators'
import { isTelephoneString } from '@/utils/validationUtils'
import { defineComponent } from 'vue'
import ButtonPanic from '@/components/interaction/ButtonPanic.vue'
import LinkFaq from '@/components/interaction/LinkFaq.vue'
import { urlService } from '@/services/urlService';


export default defineComponent({
  name: 'YourPersonalData',
  mixins: [
    LoadingMixin
  ],
  data() {
    return {
      salutation: this.$store.state.personalData.salutation,
      title: this.$store.state.personalData.title,
      givenName: this.$store.state.personalData.givenName,
      surName: this.$store.state.personalData.surName,
      organization: this.$store.state.personalData.organization,
      email: this.$store.state.personalData.email,
      phone: this.$store.state.personalData.phone,
      accessible: this.$store.state.personalData.accessible
    }
  },
  validations() {
    return {
      salutation: {
      },
      title: {
        maxLength: maxLength(40)
      },
      givenName: {
        required,
        maxLength: maxLength(40)
      },
      surName: {
        required,
        maxLength: maxLength(40)
      },
      organization: {
        maxLength: maxLength(40)
      },
      email: {
        required,
        email,
        maxLength: maxLength(320)
      },
      phone: {
        required,
        isTelephoneString,
        maxLength: maxLength(40)
      },
      accessible: {
      },
    }
  },
  components: {
    LoadingContainer,
    LinkShoppingCart,
    ButtonAction,
    ControlInput,
    ControlSelect,
    ControlCheckbox,
    ButtonPanic,
    LinkFaq
  },
  created() {
    userService.refreshShoppingCart().then(() => {
      this.state = LoadingState.success;
    })
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  methods: {
    onSubmit() {
      validateAndFocusInvalid(this.v$, this.$el).then(this.addPersonalData)
    },
    asPersonalData(): PersonalData {
      return {
        salutation: this.salutation,
        title: this.title,
        givenName: this.givenName,
        surName: this.surName,
        organization: this.organization,
        email: this.email,
        phone: this.phone,
        accessible: this.accessible
      }
    },
    salutationOptions(): SelectOption[] {
      return createSalutationOptions()
    },
    addPersonalData() {
      this.$store.commit('setPersonalData', this.asPersonalData())
      this.$router.push('/summary')
    },
    sanitizeUrl(url: string) {
      return urlService.sanitizeThisUrl(url)
    }
  }
});
</script>
